import type { ProjectTheme } from './ecosystems';

export type EcosystemDependencyOs = 'linux' | 'macos' | 'windows';

export type EcosystemReadmeMetadata = {
    displayWidth?: number;
    displayHeight?: number;
    theme?: ProjectTheme;
};

export type EcosystemReadmeSection = {
    title: string;
    content: string;
};

export type ParsedEcosystemReadme = {
    metadata: EcosystemReadmeMetadata;
    description: string;
    sections: EcosystemReadmeSection[];
    dependenciesIntro: string;
    dependencies: Partial<Record<EcosystemDependencyOs, string>>;
    configuration: string;
    build: string;
};

const FRONTMATTER_PATTERN: RegExp = /^---\r?\n([\s\S]*?)\r?\n---\r?\n?/;

const escapeRegExp = (value: string): string => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const parseFrontmatter = (raw: string): { metadata: EcosystemReadmeMetadata; body: string } => {
    const match: RegExpMatchArray | null = raw.match(FRONTMATTER_PATTERN);
    if (!match) return { metadata: {}, body: raw };

    const metadata: EcosystemReadmeMetadata = {};
    for (const line of match[1].split('\n')) {
        const trimmed: string = line.trim();
        if (!trimmed || trimmed.startsWith('#')) continue;

        const separatorIndex: number = trimmed.indexOf(':');
        if (separatorIndex < 0) continue;

        const key: string = trimmed.slice(0, separatorIndex).trim();
        const value: string = trimmed.slice(separatorIndex + 1).trim();

        if (key === 'display_width') metadata.displayWidth = Number(value);
        else if (key === 'display_height') metadata.displayHeight = Number(value);
        else if (key === 'theme') {
            if (value === 'default' || value === 'simple') metadata.theme = value;
            else if (value === 'minimal') metadata.theme = 'simple';
        }
    }

    return { metadata, body: raw.slice(match[0].length) };
};

const getSectionBody = (markdown: string, heading: string): string => {
    const pattern: RegExp = new RegExp(`^## ${escapeRegExp(heading)}\\s*$`, 'im');
    const match: RegExpMatchArray | null = markdown.match(pattern);
    if (!match || match.index === undefined) return '';

    const start: number = match.index + match[0].length;
    const rest: string = markdown.slice(start);
    const nextHeading: RegExpMatchArray | null = rest.match(/^## /m);
    const end: number = nextHeading?.index ?? rest.length;

    return rest.slice(0, end).trim();
};

const getSectionIntro = (section: string): string => {
    const firstSubsectionIndex: number = section.search(/^### /m);
    if (firstSubsectionIndex < 0) return section.trim();
    return section.slice(0, firstSubsectionIndex).trim();
};

const getSubsectionBody = (section: string, heading: string): string => {
    const pattern: RegExp = new RegExp(`^### ${escapeRegExp(heading)}\\s*$`, 'im');
    const match: RegExpMatchArray | null = section.match(pattern);
    if (!match || match.index === undefined) return '';

    const start: number = match.index + match[0].length;
    const rest: string = section.slice(start);
    const nextHeading: RegExpMatchArray | null = rest.match(/^### /m);
    const end: number = nextHeading?.index ?? rest.length;

    return rest.slice(0, end).trim();
};

const getAllSectionTitles = (markdown: string): string[] => {
    const titles: string[] = [];
    for (const match of markdown.matchAll(/^## (.+)$/gm)) {
        titles.push(match[1].trim());
    }
    return titles;
};

export const parseEcosystemReadme = (markdown: string): ParsedEcosystemReadme => {
    const { metadata, body } = parseFrontmatter(markdown);
    const sectionTitles: string[] = getAllSectionTitles(body);
    const sections: EcosystemReadmeSection[] = sectionTitles.map((title) => ({
        title,
        content: getSectionBody(body, title)
    }));
    const dependenciesSection: string = getSectionBody(body, 'Dependencies');

    const firstHeadingIndex: number = body.search(/^## /m);
    const description: string = (firstHeadingIndex >= 0 ? body.slice(0, firstHeadingIndex) : body).trim();

    return {
        metadata,
        description,
        sections,
        dependenciesIntro: getSectionIntro(dependenciesSection),
        dependencies: {
            linux: getSubsectionBody(dependenciesSection, 'Linux'),
            macos: getSubsectionBody(dependenciesSection, 'macOS'),
            windows: getSubsectionBody(dependenciesSection, 'Windows')
        },
        configuration: getSectionBody(body, 'Configuration options') || getSectionBody(body, 'Configuration'),
        build: getSectionBody(body, 'How to build')
    };
};

export const replaceEcosystemReadmePlaceholders = (content: string, placeholders: Record<string, string>): string => {
    let result: string = content;
    for (const [key, value] of Object.entries(placeholders)) {
        result = result.replace(new RegExp(`\\{\\{${escapeRegExp(key)}\\}\\}`, 'g'), () => value);
    }
    return result;
};

export const applyEcosystemReadmePlaceholders = (
    readme: ParsedEcosystemReadme,
    placeholders: Record<string, string>
): ParsedEcosystemReadme => {
    if (Object.keys(placeholders).length === 0) return readme;

    const replace = (content: string): string => replaceEcosystemReadmePlaceholders(content, placeholders);

    return {
        metadata: readme.metadata,
        description: replace(readme.description),
        sections: readme.sections.map((section) => ({
            title: section.title,
            content: replace(section.content)
        })),
        dependenciesIntro: replace(readme.dependenciesIntro),
        dependencies: {
            linux: readme.dependencies.linux ? replace(readme.dependencies.linux) : undefined,
            macos: readme.dependencies.macos ? replace(readme.dependencies.macos) : undefined,
            windows: readme.dependencies.windows ? replace(readme.dependencies.windows) : undefined
        },
        configuration: replace(readme.configuration),
        build: replace(readme.build)
    };
};

export const getDefaultDependencyOs = (hostPlatform?: string): EcosystemDependencyOs => {
    const platform: string = (hostPlatform ?? '').toLowerCase();
    if (platform === 'darwin') return 'macos';
    if (platform === 'win32') return 'windows';
    return 'linux';
};

export type MarkdownBlock =
    | { type: 'heading'; level: number; text: string }
    | { type: 'paragraph'; text: string }
    | { type: 'code'; text: string; language?: string }
    | { type: 'list'; ordered: boolean; items: string[] }
    | { type: 'blockquote'; text: string }
    | { type: 'table'; header: string[]; rows: string[][] };

const isBlankLine = (line: string): boolean => !line.trim();

const isTableRow = (line: string): boolean => {
    const trimmed: string = line.trim();
    return trimmed.startsWith('|') && trimmed.endsWith('|') && trimmed.includes('|', 1);
};

const isTableSeparatorRow = (line: string): boolean => /^\|(?:\s*:?-{3,}:?\s*\|)+\s*$/.test(line.trim());

const parseTableRow = (line: string): string[] =>
    line
        .trim()
        .replace(/^\|/, '')
        .replace(/\|$/, '')
        .split('|')
        .map((cell) => cell.trim());

const isBlockStarter = (line: string): boolean =>
    line.startsWith('```') ||
    /^(#{1,6})\s/.test(line) ||
    line.startsWith('>') ||
    /^(\s*[-*+]|\s*\d+\.)\s+/.test(line) ||
    isTableRow(line);

export const parseMarkdownBlocks = (markdown: string): MarkdownBlock[] => {
    const lines: string[] = markdown.replace(/\r\n/g, '\n').split('\n');
    const blocks: MarkdownBlock[] = [];
    let index = 0;

    while (index < lines.length) {
        const line: string = lines[index];

        if (isBlankLine(line)) {
            index++;
            continue;
        }

        const codeFence: RegExpMatchArray | null = line.match(/^```([\w-]*)$/);
        if (codeFence) {
            const language: string | undefined = codeFence[1] || undefined;
            const codeLines: string[] = [];
            index++;
            while (index < lines.length && !lines[index].startsWith('```')) {
                codeLines.push(lines[index]);
                index++;
            }
            blocks.push({ type: 'code', text: codeLines.join('\n'), language });
            index++;
            continue;
        }

        const heading: RegExpMatchArray | null = line.match(/^(#{1,6})\s+(.+)$/);
        if (heading) {
            blocks.push({ type: 'heading', level: heading[1].length, text: heading[2].trim() });
            index++;
            continue;
        }

        if (line.startsWith('>')) {
            const quoteLines: string[] = [];
            while (index < lines.length && lines[index].startsWith('>')) {
                quoteLines.push(lines[index].replace(/^>\s?/, ''));
                index++;
            }
            blocks.push({ type: 'blockquote', text: quoteLines.join('\n') });
            continue;
        }

        if (/^(\s*[-*+]|\s*\d+\.)\s+/.test(line)) {
            const ordered: boolean = /^\s*\d+\.\s+/.test(line);
            const items: string[] = [];
            while (index < lines.length && /^(\s*[-*+]|\s*\d+\.)\s+/.test(lines[index])) {
                items.push(lines[index].replace(/^(\s*[-*+]|\s*\d+\.)\s+/, '').trim());
                index++;
            }
            blocks.push({ type: 'list', ordered, items });
            continue;
        }

        if (isTableRow(line)) {
            const tableLines: string[] = [];
            while (index < lines.length && isTableRow(lines[index])) {
                tableLines.push(lines[index]);
                index++;
            }

            if (tableLines.length >= 2 && isTableSeparatorRow(tableLines[1])) {
                const header: string[] = parseTableRow(tableLines[0]);
                const rows: string[][] = tableLines.slice(2).map(parseTableRow);
                blocks.push({ type: 'table', header, rows });
            } else {
                blocks.push({ type: 'paragraph', text: tableLines.join(' ') });
            }
            continue;
        }

        const paragraphLines: string[] = [];
        while (index < lines.length && !isBlankLine(lines[index]) && !isBlockStarter(lines[index])) {
            paragraphLines.push(lines[index].trim());
            index++;
        }
        blocks.push({ type: 'paragraph', text: paragraphLines.join(' ') });
    }

    return blocks;
};
